
import { useNavigate } from "react-router-dom";
import { Form } from "@/components/ui/form";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { useAssessmentForm } from "./hooks/useAssessmentForm";
import { useAssessmentQueries } from "./hooks/useAssessmentQueries";
import { calculateMetrics } from "./utils/metrics";
import { AssessmentFormHeader } from "./components/AssessmentFormHeader";
import { BasicInfoCard } from "./components/BasicInfoCard";
import { FormField, FormItem, FormLabel, FormControl, FormMessage } from "@/components/ui/form";
import { useTranslations } from "@/hooks/useTranslations";
import { useLanguagePath } from "@/utils/language";

export const AssessmentForm = () => {
  const navigate = useNavigate();
  const { t } = useTranslations("assessments");
  const { getPath } = useLanguagePath();
  const { form, onSubmit, isSubmitting, id } = useAssessmentForm();
  const { students, previousAssessment, selectedStudent } = useAssessmentQueries(
    form.watch,
    id,
    form
  );

  const metrics = calculateMetrics(
    form.watch("weight"),
    form.watch("waist_measurement"),
    form.watch("hip_measurement"),
    selectedStudent,
    previousAssessment
  );

  return (
    <div className="space-y-4">
      <AssessmentFormHeader
        id={id}
        isSubmitting={isSubmitting}
        onCancel={() => navigate(getPath("/dashboard/assessments"))}
        onSubmit={form.handleSubmit(onSubmit)}
      />

      <Form {...form}>
        <form className="space-y-6">
          <BasicInfoCard form={form} students={students || []} />

          <Card className="dark:bg-gray-800 dark:border-gray-700">
            <CardHeader>
              <CardTitle className="dark:text-white">{t("form.bodyMeasurements")}</CardTitle>
              {metrics.bmi && (
                <div className="text-sm text-muted-foreground mt-2 dark:text-gray-300">
                  {t("metrics.bmi")}: {metrics.bmi} {selectedStudent && `(${t("metrics.height")}: ${selectedStudent.height}cm)`}
                </div>
              )}
              {metrics.whr && (
                <div className="text-sm text-muted-foreground dark:text-gray-300">
                  {t("metrics.whr")}: {metrics.whr}
                </div>
              )}
              {metrics.weightDiff && (
                <div className={`text-sm ${parseFloat(metrics.weightDiff) > 0 ? 'text-red-500' : 'text-green-500'}`}>
                  {parseFloat(metrics.weightDiff) > 0 ? t("metrics.weightGain") : t("metrics.weightLoss")}: {metrics.weightDiff}kg {t("metrics.since")}
                </div>
              )}
            </CardHeader>
            <CardContent className="grid gap-6 sm:grid-cols-2">
              <FormField
                control={form.control}
                name="weight"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="dark:text-gray-200">{t("form.weight")}</FormLabel>
                    <FormControl>
                      <Input type="number" step="0.1" {...field} className="dark:bg-gray-700 dark:border-gray-600 dark:text-white" />
                    </FormControl>
                    <FormMessage className="dark:text-red-400" />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="body_fat_percentage"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="dark:text-gray-200">{t("form.bodyFatPercentage")}</FormLabel>
                    <FormControl>
                      <Input type="number" step="0.1" {...field} className="dark:bg-gray-700 dark:border-gray-600 dark:text-white" />
                    </FormControl>
                    <FormMessage className="dark:text-red-400" />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="muscle_mass"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="dark:text-gray-200">{t("form.muscleMass")}</FormLabel>
                    <FormControl>
                      <Input type="number" step="0.1" {...field} className="dark:bg-gray-700 dark:border-gray-600 dark:text-white" />
                    </FormControl>
                    <FormMessage className="dark:text-red-400" />
                  </FormItem>
                )}
              />
            </CardContent>
          </Card>

          <Card className="dark:bg-gray-800 dark:border-gray-700">
            <CardHeader>
              <CardTitle className="dark:text-white">{t("form.specificMeasurements")}</CardTitle>
            </CardHeader>
            <CardContent className="grid gap-6 sm:grid-cols-2">
              <FormField
                control={form.control}
                name="chest_measurement"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="dark:text-gray-200">{t("form.chestMeasurement")}</FormLabel>
                    <FormControl>
                      <Input type="number" step="0.1" {...field} className="dark:bg-gray-700 dark:border-gray-600 dark:text-white" />
                    </FormControl>
                    <FormMessage className="dark:text-red-400" />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="waist_measurement"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="dark:text-gray-200">{t("form.waistMeasurement")}</FormLabel>
                    <FormControl>
                      <Input type="number" step="0.1" {...field} className="dark:bg-gray-700 dark:border-gray-600 dark:text-white" />
                    </FormControl>
                    <FormMessage className="dark:text-red-400" />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="hip_measurement"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="dark:text-gray-200">{t("form.hipMeasurement")}</FormLabel>
                    <FormControl>
                      <Input type="number" step="0.1" {...field} className="dark:bg-gray-700 dark:border-gray-600 dark:text-white" />
                    </FormControl>
                    <FormMessage className="dark:text-red-400" />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="arm_measurement"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="dark:text-gray-200">{t("form.armMeasurement")}</FormLabel>
                    <FormControl>
                      <Input type="number" step="0.1" {...field} className="dark:bg-gray-700 dark:border-gray-600 dark:text-white" />
                    </FormControl>
                    <FormMessage className="dark:text-red-400" />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="thigh_measurement"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="dark:text-gray-200">{t("form.thighMeasurement")}</FormLabel>
                    <FormControl>
                      <Input type="number" step="0.1" {...field} className="dark:bg-gray-700 dark:border-gray-600 dark:text-white" />
                    </FormControl>
                    <FormMessage className="dark:text-red-400" />
                  </FormItem>
                )}
              />
            </CardContent>
          </Card>

          <Card className="dark:bg-gray-800 dark:border-gray-700">
            <CardHeader>
              <CardTitle className="dark:text-white">{t("form.vitalSigns")}</CardTitle>
            </CardHeader>
            <CardContent className="grid gap-6 sm:grid-cols-2">
              <FormField
                control={form.control}
                name="blood_pressure"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="dark:text-gray-200">{t("form.bloodPressure")}</FormLabel>
                    <FormControl>
                      <Input {...field} placeholder={t("form.bloodPressurePlaceholder")} className="dark:bg-gray-700 dark:border-gray-600 dark:text-white dark:placeholder:text-gray-400" />
                    </FormControl>
                    <FormMessage className="dark:text-red-400" />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="resting_heart_rate"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="dark:text-gray-200">{t("form.restingHeartRate")}</FormLabel>
                    <FormControl>
                      <Input type="number" {...field} className="dark:bg-gray-700 dark:border-gray-600 dark:text-white" />
                    </FormControl>
                    <FormMessage className="dark:text-red-400" />
                  </FormItem>
                )}
              />
            </CardContent>
          </Card>

          <Card className="dark:bg-gray-800 dark:border-gray-700">
            <CardHeader>
              <CardTitle className="dark:text-white">{t("form.observations")}</CardTitle>
            </CardHeader>
            <CardContent>
              <FormField
                control={form.control}
                name="observations"
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <Textarea
                        {...field}
                        placeholder={t("form.observationsPlaceholder")}
                        className="min-h-[100px] dark:bg-gray-700 dark:border-gray-600 dark:text-white dark:placeholder:text-gray-400"
                      />
                    </FormControl>
                    <FormMessage className="dark:text-red-400" />
                  </FormItem>
                )}
              />
            </CardContent>
          </Card>
        </form>
      </Form>
    </div>
  );
};

